import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { GeoFix } from '../../platform/types';
import type { SkipReason } from '../domain/types';

/**
 * IndexedDB layer shared by the app AND the service worker (both bundle this
 * module; IDB is the cross-context store).
 *
 * `auth` holds the JWT so the service worker can sync while the page is
 * closed. Tradeoff acknowledged in the spec: IDB is origin-scoped and the
 * token is short-lived (1 h); queued items that outlive the token are synced
 * next time the driver opens the app and re-authenticates.
 */

export interface QueuedSubmission {
  id: string;                    // uuid
  kind: 'complete' | 'skip';
  taskId: string;
  queuedAt: number;
  fix: GeoFix | null;            // freshest fix at queue time
  /*
   * Where the proof ended up once uploaded.
   *
   * Set after a successful upload, at which point the BLOB IS DROPPED. Two
   * reasons, and the second is the one that matters:
   *
   *   - A retry no longer re-uploads. One phone tried the same stop
   *     sixteen times on 17 Sep and sent thirty-two images to do it, because
   *     every attempt started from the beginning.
   *   - The images stop being held in storage once they are safely in the
   *     bucket. Two photos per queued stop, on a phone that has just returned
   *     to the route screen and is re-rendering the day, is the memory peak
   *     where the app has been dying.
   */
  photoPath?: string | null;
  signaturePath?: string | null;
  // complete-only:
  recipientName: string | null;
  /** Every box scanned at this stop. */
  barcodes: string[];
  /**
   * Queued by an older build, which recorded a single scan. Items can already
   * be sitting in IndexedDB when this version installs — a driver who queued a
   * delivery in a dead zone yesterday must still be able to flush it today — so
   * the old shape is read, never written.
   */
  barcode?: string | null;
  /** Why there is no barcode, when there isn't one. */
  barcodeException: string | null;
  /**
   * Totes counted back in at the pickup. Optional on the record rather than
   * required: submissions queued by an older build are still sitting in
   * IndexedDB and must stay readable, exactly like `barcode` above.
   */
  totesReturned?: number | null;
  photoBlob: Blob | null;
  signatureBlob: Blob | null;
  /*
   * The same proof as plain BYTES (8 Oct). On iPhones a Blob kept in storage
   * can lose its contents ("The object can not be found here") once the app
   * has been in the background, and the delivery then waits until it is
   * retried from scratch. Bytes cannot go missing that way. New deliveries
   * store these and leave the Blob fields null; older queued items still
   * carry Blobs and are sent the old way.
   */
  photoBytes?: ArrayBuffer | null;
  photoType?: string | null;
  signatureBytes?: ArrayBuffer | null;
  signatureType?: string | null;
  // skip-only:
  skipReason: SkipReason | null;
  skipNote: string | null;
  attempts: number;
  lastError: string | null;
}

interface JwDB extends DBSchema {
  queue: { key: string; value: QueuedSubmission; indexes: { byQueuedAt: number } };
  auth: { key: string; value: { token: string; savedAt: number } };
  // Last route the server gave us, so a driver who loses signal still sees
  // their stops instead of being told there is no route today.
  /* Two snapshots share this store: 'route-today' (one route, read by the
     older screen) and 'routes-today' (the whole day). Both keys are optional so
     a phone holding either shape stays readable through a rollout. */
  cache: {
    key: string;
    // 'idscan:<id>' keys hold face scans taken without signal (idscans.ts).
    value: { routes?: unknown; savedAt: number } | Record<string, unknown>;
  };
  /*
   * Work captured at a stop but not yet submitted, keyed by task id.
   *
   * Everything here used to live in the screen's memory. A crash on the
   * signature step threw away three scans and a name; a driver who kills a
   * frozen app loses the lot. Now the stop can be resumed exactly where it was.
   */
  drafts: {
    key: string;
    value: {
      taskId: string;
      totes: string;
      barcodes: string[];
      barcodeException: string | null;
      /* Totes with nothing to scan. Kept so a resumed stop shows the same
         number of ticks the driver counted before the app died. */
      noCodeTotes?: number;
      recipient: string;
      signature: Blob | null;
      photo: Blob | null;
      savedAt: number;
    };
  };
}

let dbPromise: Promise<IDBPDatabase<JwDB>> | null = null;

function db(): Promise<IDBPDatabase<JwDB>> {
  dbPromise ??= openDB<JwDB>('jw-express', 3, {
    // Upgrades run cumulatively from whatever version is on the device, so a
    // phone already carrying v1 (with a queue in it) gains the new store
    // without losing anything.
    upgrade(d, oldVersion) {
      if (oldVersion < 1) {
        const queue = d.createObjectStore('queue', { keyPath: 'id' });
        queue.createIndex('byQueuedAt', 'queuedAt');
        d.createObjectStore('auth');
      }
      if (oldVersion < 2) {
        d.createObjectStore('cache');
      }
      if (oldVersion < 3) {
        // Added in version 3. A phone upgrading mid-shift keeps its queue and
        // its auth; only this store is new.
        d.createObjectStore('drafts', { keyPath: 'taskId' });
      }
    },
    // The phone closed storage under us (iOS does, in the background). Open
    // a new connection next time instead of reusing a dead one.
    terminated() { dbPromise = null; },
  });
  return dbPromise;
}

/*
 * STORAGE THAT RECONNECTS (5 Oct).
 *
 * The connection was opened once and kept forever. When an iPhone closed it in
 * the background, every later save failed with "The database connection is
 * closing" until the driver killed and reopened the app -- seen on two iPhones
 * (1 Oct, 5 Oct). A delivery that cannot be saved cannot be sent. Now a closed
 * connection is dropped and the operation is tried once more on a new one.
 */
async function use<T>(work: (d: IDBPDatabase<JwDB>) => Promise<T>): Promise<T> {
  try {
    return await work(await db());
  } catch (err) {
    if ((err as Error)?.name !== 'InvalidStateError') throw err;
    dbPromise = null;
    return work(await db());
  }
}

// --- auth token ---
export async function saveToken(token: string): Promise<void> {
  await use((d) => d.put('auth', { token, savedAt: Date.now() }, 'jwt'));
}
export async function getToken(): Promise<string | null> {
  return (await use((d) => d.get('auth', 'jwt')))?.token ?? null;
}
export async function clearToken(): Promise<void> {
  await use((d) => d.delete('auth', 'jwt'));
}

// --- offline route snapshot ---

/**
 * The route as the server last described it.
 *
 * A hospital basement has no signal, and a phone that drops the app from memory
 * has to rebuild the screen from somewhere. Without this the driver is told
 * "No route today" — which reads as "you have no work" rather than "I cannot
 * reach the office", and is the single most alarming thing the app could say to
 * someone standing at a delivery door.
 */
/**
 * Every route today, for when there is no signal.
 *
 * Separate from the single-route snapshot above, which the older endpoint still
 * reads: a phone that has not refreshed must keep working through a deploy.
 */
export async function saveRoutesSnapshot(routes: unknown): Promise<void> {
  try {
    await use((d) => d.put('cache', { routes, savedAt: Date.now() }, 'routes-today'));
  } catch { /* a snapshot is a convenience; never fail the request over it */ }
}

export async function getRoutesSnapshot(): Promise<
  { routes?: unknown; savedAt: number } | null
> {
  try {
    return ((await use((d) => d.get('cache', 'routes-today'))) as { routes?: unknown; savedAt: number } | undefined) ?? null;
  } catch {
    return null;
  }
}

/**
 * Anything this app no longer uses, cleared the first time it starts.
 *
 * 'route-today' held the old single-route snapshot. It kept being written on
 * every fetch long after the screen that read it was gone, so every phone is
 * carrying a stale copy of a shape nothing understands any more. Leftovers like
 * that are exactly what makes a test ambiguous.
 *
 * Drafts are swept on the same pass: one is kept so a crashed stop can be
 * resumed, but a draft from a previous day belongs to work that is over, and
 * restoring old barcodes into a stop would be worse than restoring nothing.
 */
const DRAFT_MAX_AGE_MS = 18 * 60 * 60 * 1000;   // through one shift, not two

export async function sweepLeftovers(): Promise<void> {
  try {
    const d = await db();
    await d.delete('cache', 'route-today');

    const cutoff = Date.now() - DRAFT_MAX_AGE_MS;
    const tx = d.transaction('drafts', 'readwrite');
    for (const draft of await tx.store.getAll()) {
      if (draft.savedAt < cutoff) await tx.store.delete(draft.taskId);
    }
    await tx.done;
  } catch {
    /* Housekeeping only. It must never be the reason the app fails to open. */
  }
}

// --- loose key/value in 'cache' (face scans waiting for signal, 17 Oct) ---
export async function cachePut(key: string, value: object): Promise<void> {
  await use((d) => d.put('cache', value as Record<string, unknown>, key));
}
export async function cacheGet<T>(key: string): Promise<T | undefined> {
  return (await use((d) => d.get('cache', key))) as T | undefined;
}
export async function cacheKeys(): Promise<string[]> {
  return (await use((d) => d.getAllKeys('cache'))).map(String);
}
export async function cacheDelete(key: string): Promise<void> {
  await use((d) => d.delete('cache', key));
}

// --- submission queue ---
export async function enqueue(item: QueuedSubmission): Promise<void> {
  await use((d) => d.put('queue', item));
}
export async function peekAll(): Promise<QueuedSubmission[]> {
  return use((d) => d.getAllFromIndex('queue', 'byQueuedAt'));
}
export async function remove(id: string): Promise<void> {
  await use((d) => d.delete('queue', id));
}
export async function markFailure(item: QueuedSubmission, error: string): Promise<void> {
  await use((d) => d.put('queue', {
    ...item, attempts: item.attempts + 1, lastError: error,
  }));
}
export async function queueSize(): Promise<number> {
  return use((d) => d.count('queue'));
}


/* ---------------------------------------------------------------------------
 * Drafts: what has been captured at a stop but not yet sent.
 * ------------------------------------------------------------------------- */

export interface StopDraft {
  taskId: string;
  totes: string;
  barcodes: string[];
  barcodeException: string | null;
  noCodeTotes?: number;
  recipient: string;
  signature: Blob | null;
  photo: Blob | null;
  savedAt: number;
}

export async function saveDraft(draft: Omit<StopDraft, 'savedAt'>): Promise<void> {
  try {
    await use((d) => d.put('drafts', { ...draft, savedAt: Date.now() }));
  } catch {
    /* Storage full or blocked. Losing a draft is bad; failing the capture the
       driver is in the middle of would be worse. */
  }
}

export async function getDraft(taskId: string): Promise<StopDraft | null> {
  try {
    return (await use((d) => d.get('drafts', taskId))) ?? null;
  } catch {
    return null;
  }
}

export async function clearDraft(taskId: string): Promise<void> {
  try {
    await use((d) => d.delete('drafts', taskId));
  } catch { /* nothing to do */ }
}
